<template>
  <div
    v-if="shouldRender"
    :class="asModal
      ? 'fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-4'
      : 'teacher-schedule-standalone'"
    @click.self="asModal ? close() : undefined"
  >
    <div
      :class="asModal
        ? 'bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col'
        : ''"
      :role="asModal ? 'dialog' : undefined"
      :aria-modal="asModal ? 'true' : undefined"
      :aria-label="asModal ? `Horario de ${teacherDisplayName}` : undefined"
    >
      <div
        v-if="asModal"
        class="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-b border-gray-200 shrink-0"
      >
        <h2 class="text-lg font-semibold text-gray-800">
          Horario de {{ teacherDisplayName }}
        </h2>
        <div class="flex items-center gap-2">
          <schedule-export-buttons
            :disabled="!canExport"
            :exporting="exporting"
            @pdf="downloadPdf"
            @xls="downloadXls"
          />
          <button
            type="button"
            class="text-gray-400 hover:text-gray-600 p-1 ml-1"
            aria-label="Cerrar"
            @click="close"
          >
            <i class="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>
      </div>

      <div :class="asModal ? 'overflow-y-auto p-5 flex-1 min-h-[12rem]' : ''">
        <div
          v-if="!asModal && !loading && !error"
          class="mb-4 flex flex-wrap items-center justify-between gap-2"
        >
          <schedule-export-buttons
            :disabled="!canExport"
            :exporting="exporting"
            @pdf="downloadPdf"
            @xls="downloadXls"
          />
        </div>
        <div
          v-if="showSummary"
          class="mb-4 leading-8 border border-indigo-200 bg-indigo-50 text-indigo-900 px-4 py-2 rounded-md font-medium"
        >
          <span>{{ teacherDisplayName }}</span>
          <span v-if="totalClasses > 0" class="ml-2 text-sm font-normal text-indigo-700">
            ({{ totalClasses }} {{ totalClasses === 1 ? 'clase' : 'clases' }})
          </span>
        </div>

        <div v-if="loading" class="flex justify-center items-center py-12">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
          <span class="ml-3 text-gray-600 text-sm">Cargando horario…</span>
        </div>

        <div
          v-else-if="error"
          class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {{ error }}
        </div>

        <div
          v-else-if="entriesByDay.length === 0"
          class="rounded-md border border-gray-200 bg-gray-50 px-4 py-8 text-center text-gray-500"
        >
          No hay clases asignadas en el horario.
        </div>

        <div v-else class="space-y-5">
          <section
            v-for="group in entriesByDay"
            :key="group.day.id"
            class="rounded-lg border border-gray-200 overflow-hidden shadow-sm"
          >
            <header class="px-4 py-2.5 bg-indigo-800 text-white text-sm font-semibold uppercase tracking-wide">
              {{ group.day.name }}
            </header>
            <ul class="divide-y divide-gray-100 bg-white">
              <li
                v-for="entry in group.items"
                :key="entry.id"
                class="px-4 py-3 flex flex-wrap gap-x-4 gap-y-1 items-baseline hover:bg-violet-50/50"
              >
                <span class="inline-flex items-center text-sm font-semibold text-gray-800 min-w-[7rem]">
                  <i class="fa-regular fa-clock text-gray-400 mr-2"></i>
                  {{ entry.time }}
                </span>
                <span class="text-sm font-medium text-violet-900">
                  {{ entry.subjectName }}
                </span>
                <span class="text-sm text-gray-600">
                  {{ entry.courseLabel }}
                </span>
                <span
                  v-if="entry.shiftName"
                  class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full"
                >
                  {{ entry.shiftName }}
                </span>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  exportTeacherSchedulePdf,
  exportTeacherScheduleXls,
} from '@/utils/teacherScheduleExport'
import ScheduleExportButtons from '@/components/schedule/ScheduleExportButtons.vue'

export default {
  name: 'TeacherSchedule',
  components: { ScheduleExportButtons },
  props: {
    teacherId: {
      type: [String, Number],
      required: true,
    },
    asModal: {
      type: Boolean,
      default: false,
    },
    visible: {
      type: Boolean,
      default: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    teacherName: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      error: null,
      teacher: null,
      days: [],
      shifts: [],
      schedules: [],
      classSubjects: [],
      assignments: [],
      entries: [],
      exporting: false,
    }
  },
  computed: {
    canExport() {
      return !this.loading && !this.error && this.entries.length > 0
    },
    shouldRender() {
      return !this.asModal || this.visible
    },
    showSummary() {
      return !this.compact && !this.asModal
    },
    teacherDisplayName() {
      if (this.teacherName) return this.teacherName
      const p = this.teacher?.profile ?? this.teacher?.Profile
      if (p) {
        return `${p.lastname}, ${p.firstname}`
      }
      return 'Docente'
    },
    totalClasses() {
      return this.entries.length
    },
    entriesByDay() {
      return this.days
        .map(day => ({
          day,
          items: this.entries
            .filter(e => e.dayId === day.id)
            .sort((a, b) => (a.timeStart || '').localeCompare(b.timeStart || '')),
        }))
        .filter(g => g.items.length > 0)
    },
  },
  watch: {
    teacherId: {
      immediate: true,
      handler(id) {
        if (id && this.shouldRender) {
          this.loadSchedule()
        }
      },
    },
    visible(val) {
      if (val && this.teacherId) {
        this.loadSchedule()
      }
    },
  },
  methods: {
    close() {
      this.$emit('close')
    },
    exportRows() {
      return this.entries.map(entry => {
        const day = this.days.find(d => d.id === entry.dayId)
        return {
          Día: day?.name || '',
          Horario: entry.time,
          Asignatura: entry.subjectName,
          Curso: entry.courseLabel,
          Turno: entry.shiftName || '',
        }
      })
    },
    async downloadPdf() {
      if (!this.canExport || this.exporting) return
      this.exporting = true
      try {
        await exportTeacherSchedulePdf(
          this.teacherDisplayName,
          this.exportRows()
        )
      } catch (err) {
        console.error(err)
        window.alert('No se pudo generar el PDF.')
      } finally {
        this.exporting = false
      }
    },
    async downloadXls() {
      if (!this.canExport || this.exporting) return
      this.exporting = true
      try {
        await exportTeacherScheduleXls(
          this.teacherDisplayName,
          this.exportRows()
        )
      } catch (err) {
        console.error(err)
        window.alert('No se pudo generar el archivo Excel.')
      } finally {
        this.exporting = false
      }
    },
    unwrapData(res) {
      const body = res?.data
      if (body?.data !== undefined) return body.data
      return body
    },
    buildEntries() {
      const shiftById = Object.fromEntries(
        (this.shifts || []).map(s => [s.id, s])
      )

      this.entries = (this.assignments || [])
        .map(row => {
          const slot = this.schedules.find(
            s => s.id === row.days_shifts_schedules_id
          )
          if (!slot) return null

          const cs = this.classSubjects.find(
            c => c.id === row.classes_subjects_id
          )
          const scheduleInfo = slot.Schedule ?? slot.schedule ?? {}
          const shift = shiftById[slot.shift_id]

          return {
            id:
              row.id ??
              `${row.days_shifts_schedules_id}-${row.classes_subjects_id}`,
            dayId: slot.day_id,
            time: scheduleInfo.description || '—',
            timeStart: scheduleInfo.time_start || '',
            subjectName: cs?.subject?.name || 'Sin asignatura',
            courseLabel: cs?.class
              ? `${cs.class.course} - ${cs.class.division}`
              : '—',
            shiftName: shift?.name || '',
          }
        })
        .filter(Boolean)
        .sort((a, b) => {
          const dayOrder =
            this.days.findIndex(d => d.id === a.dayId) -
            this.days.findIndex(d => d.id === b.dayId)
          if (dayOrder !== 0) return dayOrder
          return (a.timeStart || '').localeCompare(b.timeStart || '')
        })
    },
    async loadSchedule() {
      if (!this.teacherId) return

      this.loading = true
      this.error = null

      try {
        const [daysRes, shiftsRes, schedulesRes, classesRes, assignmentsRes, profileRes] =
          await Promise.all([
            this.$http.get('/days'),
            this.$http.get('/shifts'),
            this.$http.get('/schedules'),
            this.$http.get('/schedulesDays'),
            this.$http.get(`/schedules-by-teacher/${this.teacherId}`),
            this.$http.get(`/user_profile/${this.teacherId}`).catch(() => null),
          ])

        this.days = this.unwrapData(daysRes) || []
        this.shifts = this.unwrapData(shiftsRes) || []
        this.schedules = this.unwrapData(schedulesRes) || []
        this.classSubjects = this.unwrapData(classesRes) || []
        this.assignments = this.unwrapData(assignmentsRes) || []

        if (profileRes) {
          this.teacher = this.unwrapData(profileRes)
        }

        this.buildEntries()
      } catch (err) {
        console.error(err)
        this.error =
          err?.response?.data?.message ||
          'No se pudo cargar el horario del docente.'
        this.entries = []
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
